import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Zap, XCircle, CheckCircle2, Loader2, CalendarClock, Mail } from 'lucide-react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { inputCls } from '@/components/admin/commerce/shared';

const fmtWhen = (iso) => new Date(iso).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const defaultLocal = () => { const d = new Date(Date.now() + 3600000); d.setSeconds(0, 0); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };

const ConfirmDialog = ({ action, onClose, onConfirm, busy, t }) => {
  const [schedule, setSchedule] = useState(false);
  const [when, setWhen] = useState(defaultLocal);
  const [preview, setPreview] = useState(null);
  const sends = action === 'send_drafts' || action === 'generate_send';
  useEffect(() => {
    if (action) { setSchedule(false); setWhen(defaultLocal()); }
    setPreview(null);
    if (action) api.post('/admin/prospects/bulk/preview', { action }).then((r) => setPreview(r.data)).catch(() => setPreview({ count: 0, items: [], error: true }));
  }, [action]); // eslint-disable-line react-hooks/exhaustive-deps
  const count = preview?.count ?? 0;
  const invalid = schedule && (!when || new Date(when).getTime() < Date.now() + 120000);
  const titleKey = action === 'generate' ? 'prospects.bulk.confirmGenerateTitle'
    : action === 'send_drafts' ? 'prospects.bulk.confirmSendDraftsTitle'
    : 'prospects.bulk.confirmSendTitle';
  const bodyKey = action === 'generate' ? 'prospects.bulk.confirmGenerateBody'
    : action === 'send_drafts' ? 'prospects.bulk.confirmSendDraftsBody'
    : 'prospects.bulk.confirmSendBody';
  return (
    <Dialog open={!!action} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="bulk-confirm-dialog">
        <DialogHeader><DialogTitle>{t(titleKey, { defaultValue: action === 'send_drafts' ? 'Enviar rascunhos' : undefined })}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-700" data-testid="bulk-confirm-body">{count > 0 || schedule ? t(bodyKey, { count, defaultValue: count === 1 ? '1 email será enviado.' : `${count} emails serão enviados.` }) : t('prospects.bulk.noEligible')}</p>
        {sends && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-2" data-testid="bulk-preview">
            <p className="mb-1 text-xs font-semibold text-slate-700">{!preview ? 'A preparar a lista…' : `Vão receber o email (${preview.count}):`}</p>
            {preview?.items?.length > 0 && <ul className="max-h-52 overflow-y-auto text-xs text-slate-700 divide-y divide-slate-200">{preview.items.map((p) => <li key={p.id} className="py-1 flex justify-between gap-2" data-testid={`bulk-preview-item-${p.id}`}><span className="font-semibold truncate">{p.company}</span><span className="text-slate-500 truncate">{p.email}</span></li>)}</ul>}
            <p className="mt-1 text-[11px] text-slate-500">Excluídas sempre: seguimentos esgotados, fichas paradas, sem interesse e com reunião marcada.</p>
          </div>
        )}
        <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer"><input type="checkbox" checked={schedule} onChange={(e) => setSchedule(e.target.checked)} data-testid="bulk-schedule-toggle" /><CalendarClock className="h-4 w-4 text-slate-500" />{t('prospects.bulk.scheduleToggle')}</label>
        {schedule && (
          <div>
            <label className="text-xs font-semibold text-slate-600">{t('prospects.bulk.scheduleAt')}</label>
            <input type="datetime-local" className={inputCls} value={when} min={defaultLocal().slice(0, 16)} onChange={(e) => setWhen(e.target.value)} data-testid="bulk-schedule-input" />
            <p className="text-[11px] text-slate-500 mt-1">{t('prospects.bulk.scheduleHint')}</p>
            {invalid && <p className="text-xs text-rose-600 mt-1" data-testid="bulk-schedule-invalid">{t('prospects.bulk.invalidDate')}</p>}
          </div>
        )}
        <p className="text-[11px] text-slate-500 inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{t('prospects.bulk.summaryInfo')}</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="bulk-cancel-btn">{t('adminCommerce.cancel')}</Button>
          <Button onClick={() => onConfirm(schedule ? new Date(when).toISOString() : null, count)} disabled={busy || invalid || !preview || (!schedule && count === 0)} className={action === 'generate' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-emerald-600 hover:bg-emerald-700'} data-testid="bulk-start-btn">
            {busy ? '…' : schedule ? t('prospects.bulk.schedule') : sends ? `Confirmar e enviar a ${count}` : t('prospects.bulk.start')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const JobCard = ({ job, onCancel, onDismiss, t }) => {
  const pct = job.total ? Math.round((job.done / job.total) * 100) : 0;
  const running = job.status === 'running';
  return (
    <Card className={`p-4 mb-5 border ${running ? 'border-blue-200 bg-blue-50/40' : job.status === 'cancelled' ? 'border-slate-200' : 'border-emerald-200 bg-emerald-50/40'}`} data-testid="bulk-job-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className="font-semibold text-slate-900 inline-flex items-center gap-2">
          {running ? <Loader2 className="h-4 w-4 animate-spin text-blue-600" /> : job.status === 'cancelled' ? <XCircle className="h-4 w-4 text-slate-500" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
          {t(job.action === 'generate' ? 'prospects.bulk.actionGenerate' : job.action === 'send_drafts' ? 'prospects.bulk.sendDrafts' : 'prospects.bulk.actionGenerateSend', { defaultValue: job.action === 'send_drafts' ? 'Enviar rascunhos' : undefined })} · <span data-testid="bulk-job-status">{t(`prospects.bulk.${job.status}`)}</span>
        </p>
        <div className="flex gap-2">
          {running && <Button size="sm" variant="outline" onClick={onCancel} data-testid="bulk-job-cancel-btn">{t('prospects.bulk.cancel')}</Button>}
          {!running && <Button size="sm" variant="ghost" onClick={onDismiss} data-testid="bulk-job-dismiss-btn">{t('prospects.bulk.dismiss')}</Button>}
        </div>
      </div>
      <div className="h-2.5 w-full rounded-full bg-slate-200 overflow-hidden"><div className={`h-full transition-[width] duration-500 ${running ? 'bg-blue-600' : 'bg-emerald-600'}`} style={{ width: `${pct}%` }} data-testid="bulk-job-bar" /></div>
      <p className="text-xs text-slate-600 mt-2 flex flex-wrap gap-x-3" data-testid="bulk-job-progress">
        <span className="font-semibold">{t('prospects.bulk.progress', { done: job.done, total: job.total })}</span>
        <span className="text-purple-700">{t('prospects.bulk.generated', { count: job.generated })}</span>
        {(job.action === 'generate_send' || job.action === 'send_drafts') && <span className="text-emerald-700">{t('prospects.bulk.sent', { count: job.sent })}</span>}
        {job.failed > 0 && <span className="text-rose-700">{t('prospects.bulk.failed', { count: job.failed })}</span>}
        {running && job.current && <span className="text-slate-500 italic">{t('prospects.bulk.current', { company: job.current })}</span>}
      </p>
      {job.errors?.length > 0 && <ul className="mt-2 text-xs text-rose-700 list-disc pl-5" data-testid="bulk-job-errors">{job.errors.slice(-5).map((e, i) => <li key={i}><strong>{e.company}</strong>: {e.error}</li>)}</ul>}
    </Card>
  );
};

const ScheduledList = ({ jobs, onCancel, t }) => jobs.length === 0 ? null : (
  <Card className="p-4 mb-5 border border-amber-200 bg-amber-50/40" data-testid="bulk-scheduled-card">
    <p className="font-semibold text-slate-900 inline-flex items-center gap-2 mb-2"><CalendarClock className="h-4 w-4 text-amber-600" />{t('prospects.bulk.scheduledList')}</p>
    <ul className="space-y-1.5">
      {jobs.map((j) => (
        <li key={j.id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`bulk-scheduled-${j.id}`}>
          <span><strong>{t(j.action === 'generate' ? 'prospects.bulk.actionGenerate' : j.action === 'send_drafts' ? 'prospects.bulk.sendDrafts' : 'prospects.bulk.actionGenerateSend', { defaultValue: j.action === 'send_drafts' ? 'Enviar rascunhos' : undefined })}</strong> · {t('prospects.bulk.scheduledFor', { when: fmtWhen(j.scheduled_at) })}</span>
          <Button size="sm" variant="outline" onClick={() => onCancel(j)} data-testid={`bulk-scheduled-cancel-${j.id}`}>{t('prospects.bulk.cancelScheduled')}</Button>
        </li>
      ))}
    </ul>
  </Card>
);

export const useBulkOutreach = (prospects, onChanged, t) => {
  const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [scheduled, setScheduled] = useState([]);
  const [hidden, setHidden] = useState(false);
  const toContact = prospects.filter((p) => p.status === 'to_contact');
  const count = action === 'generate'
    ? toContact.filter((p) => !p.generated_message).length
    : action === 'send_drafts'
      ? toContact.filter((p) => p.email?.includes('@') && p.generated_message && !p.email_domain_mismatch && !p.unsubscribed).length
      : toContact.filter((p) => p.email && p.email.includes('@')).length;

  const onChangedRef = useRef(onChanged); onChangedRef.current = onChanged;
  const jobRef = useRef(job); jobRef.current = job;
  const loadScheduled = () => api.get('/admin/prospects/bulk/scheduled').then((r) => setScheduled(r.data)).catch(() => {});
  useEffect(() => {
    api.get('/admin/prospects/bulk/latest').then((r) => { const j = r.data; if (j && (j.status === 'running' || (j.finished_at && Date.now() - new Date(j.finished_at).getTime() < 30 * 60000))) setJob(j); }).catch(() => {});
    loadScheduled();
  }, []);
  useEffect(() => {
    if (scheduled.length === 0 && (!job || job.status !== 'running')) return undefined;
    const onlyScheduled = !(job && job.status === 'running');
    const id = setInterval(async () => {
      try {
        if (jobRef.current?.status === 'running') {
          const { data } = await api.get(`/admin/prospects/bulk/${jobRef.current.id}`); setJob(data);
          if (data.done !== jobRef.current?.done || data.status !== 'running') onChangedRef.current();
        } else if (scheduled.length) {
          const { data } = await api.get('/admin/prospects/bulk/latest'); if (data?.status === 'running') { setJob(data); setHidden(false); loadScheduled(); }
        }
      } catch (_) {}
    }, onlyScheduled ? 15000 : 2500);
    return () => clearInterval(id);
  }, [job?.id, job?.status, scheduled.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async (scheduledAt, expectedCount) => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/prospects/bulk', { action, scheduled_at: scheduledAt || undefined, confirm: true, expected_count: expectedCount });
      if (data.status === 'scheduled') { toast.success(t('prospects.bulk.scheduledOk')); loadScheduled(); } else { setJob(data); setHidden(false); toast.success(t('prospects.bulk.started')); }
      setAction(null);
    } catch (e) { toast.error(e?.response?.data?.detail || t('adminCommerce.saveError')); }
    finally { setBusy(false); }
  };
  const cancel = async (j) => { try { await api.post(`/admin/prospects/bulk/${j.id}/cancel`); loadScheduled(); } catch (_) {} };
  const running = job?.status === 'running';

  return {
    buttons: (
      <>
        <Button variant="outline" onClick={() => setAction('generate')} disabled={running} className="border-purple-300 text-purple-800 bg-purple-50 hover:bg-purple-100" data-testid="bulk-generate-btn"><Sparkles className="h-4 w-4 mr-1" />{t('prospects.bulk.generateAll')}</Button>
        <Button onClick={() => setAction('send_drafts')} disabled={running} className="bg-emerald-700 hover:bg-emerald-800" data-testid="bulk-send-drafts-btn"><Zap className="h-4 w-4 mr-1" />{t('prospects.bulk.sendDrafts', { defaultValue: 'Enviar rascunhos' })}</Button>
        <ConfirmDialog action={action} count={count} onClose={() => setAction(null)} onConfirm={start} busy={busy} t={t} />
      </>
    ),
    jobCard: (
      <>
        <ScheduledList jobs={scheduled} onCancel={cancel} t={t} />
        {job && !hidden && <JobCard job={job} onCancel={() => cancel(job)} onDismiss={() => setHidden(true)} t={t} />}
      </>
    ),
  };
};
